import {
  BRAND_IMPORT_ERROR_TEXT,
  type BrandImportErrorCode,
  type BrandImportJobView,
  type BrandImportSummary,
  type WebPageFailureReason,
} from '@mg/shared';

/**
 * The words the wizard's import banners and the Edit page's sources list say about an import (spec:95,
 * :190, :342, :351, :381-386; web.md §5.5, §6.2). Pure; the fixed sentences of the spec come from
 * `@mg/shared` (brand-import.ts), so the API and the web say the same thing.
 */

/** "a", "a and b", "a, b and c". */
export function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "1 field", "14 fields". */
function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** With nothing filled in at all. */
export const NOTHING_FILLED_TEXT = "We didn't find anything to fill in.";

/**
 * The summary after an import: "Filled 14 fields, 2 audiences and 6 guidelines. 3 sections were not
 * imported." (spec:351). Parts that are zero are left out, singulars read "1 field" and "1 section was
 * not imported", and with nothing filled it says so.
 */
export function importSummaryText(summary: BrandImportSummary): string {
  const filled = [
    summary.fields > 0 ? count(summary.fields, 'field', 'fields') : null,
    summary.audiences > 0 ? count(summary.audiences, 'audience', 'audiences') : null,
    summary.guidelines > 0 ? count(summary.guidelines, 'guideline', 'guidelines') : null,
  ].filter((part): part is string => part !== null);
  const first = filled.length > 0 ? `Filled ${joinWithAnd(filled)}.` : NOTHING_FILLED_TEXT;
  const skipped = summary.notImportedSections;
  if (skipped <= 0) return first;
  return `${first} ${skipped === 1 ? '1 section was' : `${skipped} sections were`} not imported.`;
}

/** The sources banner's second sentence (spec:95). */
export const SOURCES_BANNER_TAIL = 'Tags above each field show where its content came from.';

/**
 * The sources banner on steps 2-8 (spec:95): "Filled in from {document} and {n} web pages. Tags above
 * each field show where its content came from." Only sources that were read count; the web pages are
 * left out while there are none (all of Release 3). Null when no source was read: the banner is hidden.
 */
export function sourcesBannerText(job: Pick<BrandImportJobView, 'document' | 'pages'>): string | null {
  const parts: string[] = [];
  if (job.document && job.document.status === 'read') parts.push(job.document.fileName);
  const pagesRead = job.pages.filter((page) => page.status === 'read').length;
  if (pagesRead > 0) parts.push(count(pagesRead, 'web page', 'web pages'));
  if (parts.length === 0) return null;
  return `Filled in from ${joinWithAnd(parts)}. ${SOURCES_BANNER_TAIL}`;
}

/** A failed import's message (spec:384-386): the same words the API puts in the job's `error`. */
export function importFailureText(code: BrandImportErrorCode): string {
  return BRAND_IMPORT_ERROR_TEXT[code];
}

const PAGE_REASON_TEXT: Record<WebPageFailureReason, string> = {
  unreachable: "Couldn't reach this page",
  blocked: "This page can't be read automatically",
  no_text: 'No readable text on this page',
};

/** Why a web page was not read, short, for the sources list (spec:190). */
export function pageReasonText(reason: WebPageFailureReason): string {
  return PAGE_REASON_TEXT[reason];
}

/**
 * The multi-brand notice (spec:342): only when the document names brands and none of them matches the
 * workspace's name, so only company-level content was imported. Null otherwise (one brand, or a match).
 */
export function brandMatchText(match: BrandImportJobView['brandMatch'], workspaceName: string): string | null {
  if (!match || match.kind !== 'none') return null;
  const brands = match.brands.map((brand) => brand.trim()).filter((brand) => brand !== '');
  const covers =
    brands.length === 0
      ? `None of the brands in your document matches your workspace name, ${workspaceName}.`
      : brands.length === 1
        ? `Your document covers ${brands[0]}, which doesn't match your workspace name, ${workspaceName}.`
        : `Your document covers ${joinWithAnd(brands)}, and none of them matches your workspace name, ${workspaceName}.`;
  return `${covers} We imported only content about your whole company. Check your workspace name, then upload the document again.`;
}
